import { ReactElement, useRef, useState } from "react";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { Form, Tab, Tabs } from "react-bootstrap";
import { FieldErrors, useForm } from "react-hook-form";
import { tPaymentProviders } from "../../../../i18n";
import {
  AddOrEditItemProps,
  AddOrEditMode,
  ProviderFormValidationCommon,
  mapToAddPaymentMethodConfiguration,
  mapToPaymentMethodConfiguration,
} from "../ProvidersCommon/Common";
import ItemFilterGroup from "../../../../models/itemFiltering/itemFilterGroup";
import { PaymentProvider } from "../../../../models/paymentProviders/paymentProvider";
import TotalProcessingGeneralSettingsTab from "./TotalProcessingGeneralSettingsTab";
import { addConfiguration, updateConfiguration } from "../../../../services/paymentProviders/paymentProviders.service";
import TotalProcessingOptionsTab from "./TotalProcessingOptionsTab";
import { TabArray, selectTabFromValidationErrors } from "../../../../helpers/hookForms/hookFormHelpers";
import {
  CurrentPaymentProviderConfiguration,
  CurrentFormModel,
  CurrentProviderModelConsts,
  CurrentProviderDisplayName,
  mapToAddConfiguration,
  mapToUpdateConfiguration,
  CurrentPaymentMethodType,
} from "./totalProcessingConfiguration";
import { DefaultProvider } from "../DefaultProvider";
import { PageTitle } from "../../../common/PageTitle";
import PaymentProviderFiltersDialog from "../ProvidersCommon/PaymentProviderFiltersDialog";
import ProviderButtonToolbar from "../ProvidersCommon/ProviderButtonToolbar";
import TabRef from "../../../../components/Tabs/TabWithReference";

const EventKeyGeneralSettings = "general";
const EventKeyOptions = "options";

interface CurrentItemProps extends AddOrEditItemProps<CurrentPaymentProviderConfiguration, CurrentFormModel> {
  mode: AddOrEditMode;
  data?: CurrentPaymentProviderConfiguration;
  paymentProvider?: PaymentProvider;
  onSave: () => void;
}

/*
 * Form validation schema
 */
const validationSchema = Yup.object({
  ...ProviderFormValidationCommon,
  useSandbox: Yup.bool().label("Use Sandbox"),
  entityId: Yup.string()
    .label("Entity Id")
    .required()
    .min(CurrentProviderModelConsts.EntityIdMinLength)
    .max(CurrentProviderModelConsts.EntityIdMaxLength),
  accessToken: Yup.string()
    .label("Access Token")
    .required()
    .min(CurrentProviderModelConsts.AccessTokenMinLength)
    .max(CurrentProviderModelConsts.AccessTokenMaxLength),
  storePayerPaymentMethod: Yup.boolean(),
  saveCardOptionDefault: Yup.boolean(),
}).required();

/*
 * Control to display the tabs for the Total Processing payment provider
 */
const TotalProcessingItem = ({ mode, data, paymentProvider, onSave }: CurrentItemProps): ReactElement => {
  const { t } = useTranslation(tPaymentProviders.ns);
  const [selectedTab, setSelectedTab] = useState<string | null>(EventKeyGeneralSettings);

  // For now Total Processing only supports one payment method type
  const paymentMethodConfiguration = data?.paymentMethodConfigurations.find(
    (x) => x.paymentMethodType === CurrentPaymentMethodType
  );

  const [filterGroup, setFilterGroup] = useState<ItemFilterGroup | undefined>(paymentMethodConfiguration?.filterGroup);
  const [showFilterDialog, setShowFilterDialog] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const tabRefs = useRef<TabArray>([]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ resolver: yupResolver(validationSchema) });

  // Saves the form data
  const handleSave = async (model: CurrentFormModel): Promise<void> => {
    setSaving(true);
    if (!data) {
      const addConfig = mapToAddConfiguration(model);
      const addPaymentMethod = mapToAddPaymentMethodConfiguration(CurrentPaymentMethodType, model.enabled, filterGroup);
      addConfig.paymentMethodConfigurations = [addPaymentMethod];
      if (await addConfiguration(addConfig).finally(() => setSaving(false))) {
        onSave();
      }
    } else {
      const configuration = mapToUpdateConfiguration(data, model);
      const paymentMethod = mapToPaymentMethodConfiguration(paymentMethodConfiguration!, model.enabled, filterGroup);
      configuration.paymentMethodConfigurations = [paymentMethod];
      await updateConfiguration(configuration).finally(() => setSaving(false));
      onSave();
    }
  };

  // Handles validation errors on the form, and selects the tab with the first error
  const handleValidationErrors = (errorData: FieldErrors<CurrentFormModel>): void => {
    selectTabFromValidationErrors(tabRefs, errorData, setSelectedTab);
  };

  const addMode = mode === "add";
  const providerId = data?.id;

  return (
    <section className='custom-connect-page'>
      <DefaultProvider paymentProviderId={providerId} allowSetDefault>
        <PageTitle
          title={t(addMode ? "paymentProviders.titleAddConfiguration" : "paymentProviders.titleEditConfiguration", {
            providerName: CurrentProviderDisplayName,
          })}
        />
      </DefaultProvider>
      <Form
        id='total-processing-form'
        className='mt-2'
        noValidate
        onSubmit={handleSubmit(
          (submitData) => handleSave(submitData as CurrentFormModel),
          (errorData) => handleValidationErrors(errorData)
        )}
      >
        <Tabs
          defaultActiveKey={EventKeyGeneralSettings}
          className='mb-2'
          activeKey={selectedTab ?? EventKeyGeneralSettings}
          onSelect={(tab) => setSelectedTab(tab)}
        >
          <Tab eventKey={EventKeyGeneralSettings} title={t("commonTableTitles.generalSettingsTabTitle")}>
            <TabRef eventKey={EventKeyGeneralSettings} index={0} tabRefs={tabRefs}>
              <TotalProcessingGeneralSettingsTab
                mode={mode}
                data={data}
                paymentProvider={paymentProvider}
                itemFilterGroup={filterGroup}
                onShowFilterDialog={() => setShowFilterDialog(true)}
                register={register}
                errors={errors}
              />
            </TabRef>
          </Tab>
          <Tab eventKey={EventKeyOptions} title={t("commonTableTitles.optionsTabTitle")}>
            <TabRef eventKey={EventKeyOptions} index={1} tabRefs={tabRefs}>
              <TotalProcessingOptionsTab data={data} register={register} watch={watch} />
            </TabRef>
          </Tab>
        </Tabs>
        <ProviderButtonToolbar saving={saving} />
      </Form>
      <PaymentProviderFiltersDialog
        filterGroup={filterGroup}
        show={showFilterDialog}
        onUpdate={(group) => {
          setShowFilterDialog(false);
          setFilterGroup(group);
        }}
        onClose={() => setShowFilterDialog(false)}
      />
    </section>
  );
};

TotalProcessingItem.defaultProps = {
  data: undefined,
  paymentProvider: undefined,
};

export default TotalProcessingItem;
